<!-- Controls.vue -->
<template>
  <div class="controls-wrapper">
    <!-- Separate Timeline for Portrait/Mobile (shown above controls) -->
    <div class="timeline-mobile-wrapper">
      <div class="timeline-container timeline-mobile" ref="timelineContainerEl">
        <!-- Timeline Skeleton Loader -->
        <div v-if="!controlsReady" class="timeline-skeleton">
          <div class="skeleton-segments">
            <div class="skeleton-segment" v-for="i in 8" :key="i" :style="{ left: `${i * 12}%`, width: `${8 + (i % 3) * 2}%` }"></div>
          </div>
          <div class="skeleton-center-line"></div>
          <div class="skeleton-shimmer"></div>
        </div>
        
        <!-- Actual Timeline -->
        <div v-else class="timeline-new" @wheel.prevent="onWheel" @mousedown="onDragStart" @touchstart.passive="onTouchStart">
          <!-- Wrapper for segments/ticks that can be transformed during drag -->
          <div class="timeline-content" :style="{ transform: `translateX(${dragOffset}px)` }">
            <div class="segments">
              <div v-for="seg in paintedSegments" :key="seg.key" class="segment" :style="{ left: seg.x+'px', width: seg.w+'px' }"/>
            </div>
            <div class="ticks">
              <div v-for="t in visibleTicks" :key="t.key" class="tick" :style="{ left: t.px + 'px' }">
                <div class="tick-line" :class="{ major: t.major }"></div>
                <div class="tick-label">{{ t.label }}</div>
              </div>
            </div>
          </div>
          <div class="center-line"></div>
        </div>
      </div>
    </div>

    <!-- Main Controls Bar -->
    <div class="controls-bar">
      <!-- Play/Pause Button -->
      <button class="control-btn play-pause" @click="togglePlayPause" :disabled="!controlsReady" title="Play/Pause">
        <span class="mdi" :class="isPlaying ? 'mdi-pause' : 'mdi-play'"></span>
      </button>

      <!-- Timeline Mode Toggle -->
      <button v-if="allowDebug" class="control-btn timeline-mode" @click="store.setContinuousMode(!store.continuousMode)" :disabled="!controlsReady" :title="store.continuousMode ? 'Switch to Real-time Mode' : 'Switch to Continuous Mode'">
        <span class="mdi" :class="store.continuousMode ? 'mdi-minus' : 'mdi-dots-horizontal'"></span>
      </button>

      <!-- Timeline (for Landscape/Desktop) -->
      <div class="timeline-container timeline-desktop" ref="timelineDesktopEl">
        <!-- Timeline Skeleton Loader -->
        <div v-if="!controlsReady" class="timeline-skeleton">
          <!-- <div class="skeleton-segments">
            <div class="skeleton-segment" v-for="i in 8" :key="i" :style="{ left: `${i * 12}%`, width: `${8 + (i % 3) * 2}%` }"></div>
          </div> -->
          <div class="skeleton-center-line"></div>
          <div class="skeleton-shimmer"></div>
        </div>
        
        <!-- Actual Timeline -->
        <div v-else class="timeline-new" @wheel.prevent="onWheel" @mousedown="onDragStart" @touchstart.passive="onTouchStart">
          <!-- Wrapper for segments/ticks that can be transformed during drag -->
          <div class="timeline-content" :style="{ transform: `translateX(${dragOffset}px)` }">
            <div class="segments">
              <div v-for="seg in paintedSegments" :key="seg.key" class="segment" :style="{ left: seg.x+'px', width: seg.w+'px' }"/>
            </div>
            <div class="ticks">
              <div v-for="t in visibleTicks" :key="t.key" class="tick" :style="{ left: t.px + 'px' }">
                <div class="tick-line" :class="{ major: t.major }"></div>
                <div class="tick-label">{{ t.label }}</div>
              </div>
            </div>
          </div>
          <div class="center-line"></div>
        </div>
      </div>

      <!-- Compact Day Selector -->
      <div class="day-selector-compact">
        <button 
          class="day-nav-btn-small" 
          @click="goToPreviousDay" 
          :disabled="!controlsReady || !hasPreviousDay"
          title="Previous day"
        >
          <span class="mdi mdi-chevron-left"></span>
        </button>
        
        <button class="day-button" @click="toggleDayPicker" :disabled="!controlsReady" :title="currentDayFormatted">
          <span class="mdi mdi-calendar"></span>
          <span class="day-short">{{ currentDayShort }}</span>
        </button>
        
        <button 
          class="day-nav-btn-small" 
          @click="goToNextDay" 
          :disabled="!controlsReady || !hasNextDay"
          title="Next day"
        >
          <span class="mdi mdi-chevron-right"></span>
        </button>
        
        <!-- Day picker dropdown -->
        <div v-if="showDayPicker" class="day-picker" ref="dayPickerEl">
          <div 
            v-for="day in availableDays" 
            :key="day.dateString" 
            class="day-option"
            :class="{ active: day.dateString === currentDayString }"
            @click="selectDay(day.dateString)"
          >
            {{ formatDayOption(day.dateString) }}
          </div>
        </div>
      </div>

      <!-- Download Button -->
      <button class="control-btn download" @click="openDownloadModal" :disabled="!controlsReady" title="Скачать отрезок">
        <span class="mdi mdi-download"></span>
      </button>

      <!-- Screenshot Button -->
      <button class="control-btn screenshot" @click="takeScreenshot" :disabled="!controlsReady" title="Сделать скриншот">
        <span class="mdi mdi-camera"></span>
      </button>

      <!-- Zoom Button -->
      <button class="control-btn zoom" :class="{ 'zoom-active': videoZoomLevel > 1 }" @click="toggleZoom" :disabled="!controlsReady" title="Увеличить видео">
        <span class="mdi" :class="videoZoomLevel !== 3 ? 'mdi-magnify-plus' : 'mdi-magnify-minus'"></span>
      </button>

      <!-- Playback Speed Button -->
      <div class="speed-control-wrapper">
        <button class="control-btn speed" :class="{ 'speed-active': playbackSpeed !== 1 }" @click="toggleSpeedSlider" :disabled="!controlsReady" title="Скорость воспроизведения">
          <span class="speed-label">{{ playbackSpeed }}x</span>
        </button>
        
        <!-- Speed Slider Popup -->
        <div v-if="showSpeedSlider" class="speed-slider-popup" @click.stop>
          <div class="speed-slider-header">
            <span>Скорость воспроизведения</span>
            <button @click="showSpeedSlider = false" class="speed-close">
              <span class="mdi mdi-close"></span>
            </button>
          </div>
          <div class="speed-slider-body">
            <div class="speed-presets">
              <button 
                v-for="preset in speedPresets" 
                :key="preset"
                class="speed-preset-btn"
                :class="{ 'active': playbackSpeed === preset }"
                @click="setPlaybackSpeed(preset)"
              >
                {{ preset }}x
              </button>
            </div>
            <div class="speed-slider-container">
              <input 
                type="range" 
                min="0" 
                max="15" 
                step="1"
                :value="speedToSliderValue(playbackSpeed)"
                @input="handleSpeedSliderChange"
                class="speed-slider"
              />
            </div>
          </div>
        </div>
      </div>

             <!-- Fullscreen Button -->
            <button class="control-btn fullscreen" @click="toggleFullscreen" :disabled="!controlsReady" title="Полноэкранный режим">
        <span class="mdi" :class="isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"></span>
       </button>
    </div>
  </div>

  <!-- Download Modal (outside controls-wrapper to prevent opacity issues) -->
  <Teleport to="body">
    <DownloadModal
      :show="showDownloadModal"
      :url="props.url"
      :camera-id="props.cameraId"
      :current-day="currentDayString"
      :available-days="availableDays"
      :all-segments="allSegments"
      :server-tz="props.serverTz"
      :initial-from-seconds="downloadFromSeconds"
      :initial-to-seconds="downloadToSeconds"
      @close="showDownloadModal = false"
    />
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useDefaultStore } from '@/store';
import DownloadModal from './DownloadModal.vue';

const store = useDefaultStore();

const props = defineProps({
  cameraId: { type: String, required: true },
  serverTz: { type: String, default: 'UTC' },
  timelineData: { type: Array, required: true },
  modelValue: { type: Number, required: true }, // epoch ms
  width: { type: Number, default: 900 },
  url: { type: String, required: true },
  videoElement: { type: Object, default: null },
  containerElement: { type: Object, default: null },
  controlsReady: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'seek', 'play', 'pause', 'fullscreen', 'video-zoom', 'playback-speed']);

// Playback state
const isPlaying = ref(false);
const isFullscreen = ref(false);
const currentPlaybackDay = ref('');
let videoEventListeners = [];
const suppressTimelineSync = ref(false);

// Video zoom state
const videoZoomLevel = ref(1); // 1 = no zoom, 2 = 2x, 3 = 3x
const videoZoomPan = ref({ x: 0, y: 0 }); // Pan offset in percentage

// Playback speed state
const playbackSpeed = ref(1);
const showSpeedSlider = ref(false);
const speedPresets = [1, 2, 4, 8, 16];

// Speed values mapping (exponential scale for smoother control)
const speedValues = [1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5, 6, 7, 8, 10, 12, 14, 16];

// Timeline mode (false = real-time with gaps, true = continuous)
const allowDebug = computed(() => {
  return store.isAdmin;
});

// Download modal state
const showDownloadModal = ref(false);
const downloadFromSeconds = ref(0);
const downloadToSeconds = ref(0);

// Update playing state from video element and sync timeline
watch(() => props.videoElement, (videoEl, oldVideoEl) => {
  // Cleanup old listeners
  if (oldVideoEl && videoEventListeners.length > 0) {
    videoEventListeners.forEach(({ event, handler }) => {
      oldVideoEl.removeEventListener(event, handler);
    });
    videoEventListeners = [];
  }
  
  if (!videoEl) return;
  
  const updatePlayingState = () => {
    isPlaying.value = !videoEl.paused;
  };
  
  const handleTimeUpdate = () => {
    if (videoEl.seeking || suppressTimelineSync.value || dragging) return;
    if (!videoEl.paused && currentPlaybackDay.value) {
      syncTimelineWithVideo(videoEl.currentTime);
    }
  };

  const handleSeeking = () => {
    suppressTimelineSync.value = true;
  };

  const handleSeeked = () => {
    suppressTimelineSync.value = false;
  };
  
  // Add event listeners
  const listeners = [
    { event: 'play', handler: updatePlayingState },
    { event: 'pause', handler: updatePlayingState },
    { event: 'ended', handler: updatePlayingState },
    { event: 'seeking', handler: handleSeeking },
    { event: 'seeked', handler: handleSeeked },
    { event: 'playing', handler: handleSeeked },
    { event: 'canplay', handler: handleSeeked },
    { event: 'timeupdate', handler: handleTimeUpdate },
  ];
  
  listeners.forEach(({ event, handler }) => {
    videoEl.addEventListener(event, handler);
  });
  
  videoEventListeners = listeners;
  
  // Initial state
  updatePlayingState();
}, { immediate: true });

// Sync timeline position with video playback
function syncTimelineWithVideo(videoCurrentTime) {
  // Get the day's segments
  const daySegments = allSegments.value.filter(seg => {
    const segDay = getDayParts(seg.startMs);
    const segDayStr = `${segDay.find(p => p.type === 'year')?.value}-${segDay.find(p => p.type === 'month')?.value}-${segDay.find(p => p.type === 'day')?.value}`;
    return segDayStr === currentPlaybackDay.value;
  });
  
  if (daySegments.length === 0) return;
  
  // Convert video time (seconds) back to absolute timestamp
  let accumulatedSeconds = 0;
  
  for (const segment of daySegments) {
    const segmentDuration = (segment.endMs - segment.startMs) / 1000;
    
    if (videoCurrentTime < accumulatedSeconds + segmentDuration) {
      // We're in this segment
      const offsetInSegment = videoCurrentTime - accumulatedSeconds;
      const absoluteTimestamp = segment.startMs + (offsetInSegment * 1000);
      
      // Update timeline position without triggering a seek
      emit('update:modelValue', absoluteTimestamp);
      return;
    }
    
    accumulatedSeconds += segmentDuration;
  }
}

// Play/Pause toggle
function togglePlayPause() {
  if (props.videoElement) {
    if (isPlaying.value) {
      props.videoElement.pause();
      emit('pause');
    } else {
      props.videoElement.play().catch(() => {});
      emit('play');
    }
  }
}

// Fullscreen toggle
function toggleFullscreen() {
  if (!props.containerElement) return;
  
  if (!document.fullscreenElement) {
    props.containerElement.requestFullscreen().then(() => {
      isFullscreen.value = true;
      emit('fullscreen', true);
    }).catch(() => {});
  } else {
    document.exitFullscreen().then(() => {
      isFullscreen.value = false;
      emit('fullscreen', false);
    }).catch(() => {});
  }
}

// Open download modal
function openDownloadModal() {
  // Calculate seconds from start of day for current position
  const currentDate = new Date(props.modelValue);
  
  // Get start of day in server timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: props.serverTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(currentDate);
  const hour = parts.find(p => p.type === 'hour').value;
  const minute = parts.find(p => p.type === 'minute').value;
  const second = parts.find(p => p.type === 'second').value;
  
  // Calculate seconds from start of day
  const currentSeconds = parseInt(hour) * 3600 + parseInt(minute) * 60 + parseInt(second);
  
  // Set default range: current time +/- 30 minutes (1800 seconds)
  const rangeBuffer = 1800;
  downloadFromSeconds.value = Math.max(0, currentSeconds - rangeBuffer);
  downloadToSeconds.value = Math.min(86399, currentSeconds + rangeBuffer); // 86399 = 23:59:59
  
  showDownloadModal.value = true;
}

// Take screenshot function
function takeScreenshot() {
  if (!props.videoElement) {
    console.error('No video element available for screenshot');
    return;
  }

  const video = props.videoElement;
  
  // Create canvas with video dimensions
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  
  // Draw current video frame to canvas
  const ctx = canvas.getContext('2d');
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  
  // Generate filename with camera name and timestamp
  const now = new Date(props.modelValue);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: props.serverTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const formattedDate = formatter.format(now).replace(/[/:]/g, '-').replace(/,/g, '').replace(/ /g, '_');
  const filename = `${props.cameraId}_${formattedDate}.jpg`;
  
  // Convert canvas to blob and trigger download
  canvas.toBlob((blob) => {
    if (!blob) {
      console.error('Failed to create screenshot blob');
      return;
    }
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    
    // Clean up
    URL.revokeObjectURL(url);
  }, 'image/jpeg', 0.95); // 95% quality
}

// Video zoom toggle
function toggleZoom() {
  // Cycle through zoom levels: 1x -> 2x -> 3x -> 1x
  if (videoZoomLevel.value === 1) {
    videoZoomLevel.value = 2;
  } else if (videoZoomLevel.value === 2) {
    videoZoomLevel.value = 3;
  } else {
    videoZoomLevel.value = 1;
    videoZoomPan.value = { x: 0, y: 0 }; // Reset pan when zooming out
  }
  
  // Constrain pan to new zoom level's limits
  if (videoZoomLevel.value > 1 && props.videoElement) {
    const { maxPanX, maxPanY } = calculateMaxPan(props.videoElement, videoZoomLevel.value);
    videoZoomPan.value = {
      x: Math.max(-maxPanX, Math.min(maxPanX, videoZoomPan.value.x)),
      y: Math.max(-maxPanY, Math.min(maxPanY, videoZoomPan.value.y))
    };
  }
  
  emitZoomState();
}

// Emit zoom state to parent
function emitZoomState(isCurrentlyPanning = false) {
  emit('video-zoom', {
    level: videoZoomLevel.value,
    pan: { ...videoZoomPan.value },
    isPanning: isCurrentlyPanning
  });
}

// Playback speed functions
function toggleSpeedSlider() {
  showSpeedSlider.value = !showSpeedSlider.value;
}

function setPlaybackSpeed(speed) {
  playbackSpeed.value = speed;
  emitPlaybackSpeed();
}

function speedToSliderValue(speed) {
  return speedValues.indexOf(speed);
}

function sliderValueToSpeed(value) {
  return speedValues[value];
}

function handleSpeedSliderChange(e) {
  const value = parseInt(e.target.value);
  const speed = sliderValueToSpeed(value);
  playbackSpeed.value = speed;
  emitPlaybackSpeed();
}

function emitPlaybackSpeed() {
  emit('playback-speed', playbackSpeed.value);
}

// Close speed slider when clicking outside
watch(showSpeedSlider, (isOpen) => {
  if (isOpen) {
    const handleClickOutside = (e) => {
      const speedControl = document.querySelector('.speed-control-wrapper');
      if (speedControl && !speedControl.contains(e.target)) {
        showSpeedSlider.value = false;
        document.removeEventListener('click', handleClickOutside);
      }
    };
    // Add listener on next tick to avoid immediate closing
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
  }
});

// Video pan state for dragging
let isPanning = false;
let panStartPos = { x: 0, y: 0 };
let panStartOffset = { x: 0, y: 0 };

// Calculate maximum pan values based on zoom level and video aspect ratio
function calculateMaxPan(videoElement, zoomLevel) {
  if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
    // Fallback to simple calculation if video dimensions not available
    const maxPan = ((zoomLevel - 1) / zoomLevel) * 50;
    return { maxPanX: maxPan, maxPanY: maxPan };
  }
  
  // Get video dimensions
  const videoWidth = videoElement.videoWidth;
  const videoHeight = videoElement.videoHeight;
  const videoAspect = videoWidth / videoHeight;
  
  // Get container dimensions
  const containerWidth = videoElement.clientWidth;
  const containerHeight = videoElement.clientHeight;
  const containerAspect = containerWidth / containerHeight;
  
  // Determine which dimension is constrained (object-fit: contain)
  let displayedWidth, displayedHeight;
  
  if (videoAspect > containerAspect) {
    // Video is wider - constrained by width
    displayedWidth = containerWidth;
    displayedHeight = containerWidth / videoAspect;
  } else {
    // Video is taller - constrained by height
    displayedHeight = containerHeight;
    displayedWidth = containerHeight * videoAspect;
  }
  
  // Calculate how much of the container is actually filled by video
  const fillRatioX = displayedWidth / containerWidth;
  const fillRatioY = displayedHeight / containerHeight;
  
  // Calculate maximum pan for each axis
  // The formula: at zoom level Z, we can pan (Z-1)/Z * 50% * fillRatio
  const basePan = ((zoomLevel - 1) / zoomLevel) * 50;
  const maxPanX = basePan * fillRatioX;
  const maxPanY = basePan * fillRatioY;
  
  return { maxPanX, maxPanY };
}

// Setup video pan handlers
watch(() => props.videoElement, (videoEl, oldVideoEl) => {
  // Cleanup old handlers
  if (oldVideoEl) {
    oldVideoEl.style.cursor = '';
    // Handlers are on video element, will be cleaned up
  }
  
  if (!videoEl) return;
  
  // Mouse pan handlers
  const handleMouseDown = (e) => {
    if (videoZoomLevel.value === 1) return; // Only pan when zoomed
    
    isPanning = true;
    panStartPos = { x: e.clientX, y: e.clientY };
    panStartOffset = { ...videoZoomPan.value };
    videoEl.style.cursor = 'grabbing';
    e.preventDefault();
  };
  
  const handleMouseMove = (e) => {
    if (!isPanning) return;
    
    // Calculate pan delta as percentage of video size
    const deltaX = (e.clientX - panStartPos.x) / videoEl.clientWidth * 100;
    const deltaY = (e.clientY - panStartPos.y) / videoEl.clientHeight * 100;
    
    // Calculate maximum pan based on zoom level and aspect ratio
    const { maxPanX, maxPanY } = calculateMaxPan(videoEl, videoZoomLevel.value);
    
    videoZoomPan.value = {
      x: Math.max(-maxPanX, Math.min(maxPanX, panStartOffset.x + deltaX)),
      y: Math.max(-maxPanY, Math.min(maxPanY, panStartOffset.y + deltaY))
    };
    
    emitZoomState(true); // Pass true to indicate we're currently panning
  };
  
  const handleMouseUp = () => {
    if (!isPanning) return;
    isPanning = false;
    emitZoomState(false); // Pass false when panning ends
    if (videoEl) {
      videoEl.style.cursor = videoZoomLevel.value > 1 ? 'grab' : '';
    }
  };
  
  // Touch pan handlers
  const handleTouchStart = (e) => {
    if (videoZoomLevel.value === 1) return;
    if (e.touches.length !== 1) return; // Only single finger pan
    
    isPanning = true;
    const touch = e.touches[0];
    panStartPos = { x: touch.clientX, y: touch.clientY };
    panStartOffset = { ...videoZoomPan.value };
    e.preventDefault();
  };
  
  const handleTouchMove = (e) => {
    if (!isPanning || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    const deltaX = (touch.clientX - panStartPos.x) / videoEl.clientWidth * 100;
    const deltaY = (touch.clientY - panStartPos.y) / videoEl.clientHeight * 100;
    
    // Calculate maximum pan based on zoom level and aspect ratio
    const { maxPanX, maxPanY } = calculateMaxPan(videoEl, videoZoomLevel.value);
    
    videoZoomPan.value = {
      x: Math.max(-maxPanX, Math.min(maxPanX, panStartOffset.x + deltaX)),
      y: Math.max(-maxPanY, Math.min(maxPanY, panStartOffset.y + deltaY))
    };
    
    emitZoomState(true); // Pass true to indicate we're currently panning
    e.preventDefault();
  };
  
  const handleTouchEnd = () => {
    if (isPanning) {
      emitZoomState(false); // Pass false when panning ends
    }
    isPanning = false;
  };
  
  // Add event listeners
  videoEl.addEventListener('mousedown', handleMouseDown);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
  videoEl.addEventListener('touchstart', handleTouchStart, { passive: false });
  videoEl.addEventListener('touchmove', handleTouchMove, { passive: false });
  videoEl.addEventListener('touchend', handleTouchEnd);
  
  // Update cursor when zoom changes
  watch(videoZoomLevel, (level) => {
    if (videoEl) {
      videoEl.style.cursor = level > 1 ? 'grab' : '';
    }
  });
}, { immediate: true });

// Listen for fullscreen changes
onMounted(() => {
  const handleFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement;
  };
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  
  onBeforeUnmount(() => {
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
  });
});

// Timeline zoom - simplified
const pxPerSecond = ref(0.1); // Default zoom level
const currentMs = ref(props.modelValue);
const timelineContainerEl = ref(null); // Mobile timeline ref
const timelineDesktopEl = ref(null); // Desktop timeline ref
const actualTimelineWidth = ref(900); // Will be updated with actual width

// Zoom levels: 5 min, 15 min, 30 min, 1 hour, 2 hours, 4 hours, 8 hours, 12 hours
const minZoom = 0.01; // Very zoomed out (hours)
const maxZoom = 3.0;  // Very zoomed in (seconds)

// global segments across all days: [{ startMs, endMs }]
const allSegments = ref([]);
function normalizeTimestampMs(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  // Treat second-based timestamps as legacy input and convert to ms.
  return num < 1e11 ? num * 1000 : num;
}

function normalizeSegment(raw) {
  const startMs = normalizeTimestampMs(raw?.startMs);
  const endMs = normalizeTimestampMs(raw?.endMs);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  if (endMs <= startMs) return null;
  const maxDurationMs = 24 * 60 * 60 * 1000;
  const safeEndMs = endMs - startMs > maxDurationMs ? startMs + maxDurationMs : endMs;
  return { ...raw, startMs, endMs: safeEndMs };
}

async function fetchWindow() {
  const flattened = props.timelineData
    .flatMap(d => d?.segments || [])
    .map(normalizeSegment)
    .filter(Boolean);

  allSegments.value = flattened;
  
  // Sort segments by start time
  allSegments.value.sort((a, b) => a.startMs - b.startMs);
}

// Helper: Find which segment contains the given timestamp
function findSegmentForTimestamp(timestampMs) {
  return allSegments.value.find(seg => 
    timestampMs >= seg.startMs && timestampMs <= seg.endMs
  );
}

// Helper: Snap to nearest valid segment if timestamp is in a gap
// Respects day boundaries to avoid jumping between days
function snapToNearestSegment(timestampMs) {
  const segment = findSegmentForTimestamp(timestampMs);
  if (segment) return timestampMs; // Already in valid segment

  if (allSegments.value.length === 0) return timestampMs;

  // Get the day of the timestamp we're trying to snap
  const targetDayParts = getDayParts(timestampMs);
  const targetDayString = `${targetDayParts.find(p => p.type === 'year')?.value}-${targetDayParts.find(p => p.type === 'month')?.value}-${targetDayParts.find(p => p.type === 'day')?.value}`;
  
  // First, try to find segments from the same day
  const sameDaySegments = allSegments.value.filter(seg => {
    const segDay = getDayParts(seg.startMs);
    const segDayStr = `${segDay.find(p => p.type === 'year')?.value}-${segDay.find(p => p.type === 'month')?.value}-${segDay.find(p => p.type === 'day')?.value}`;
    return segDayStr === targetDayString;
  });

  // If there are segments on this day, snap to the closest edge within the same day
  if (sameDaySegments.length > 0) {
    let closestEdge = null;
    let minDistance = Infinity;
    let isStart = false;
    
    for (const seg of sameDaySegments) {
      // Check distance to segment start
      const distanceToStart = Math.abs(timestampMs - seg.startMs);
      if (distanceToStart < minDistance || (distanceToStart === minDistance && !isStart)) {
        minDistance = distanceToStart;
        closestEdge = seg.startMs;
        isStart = true;
      }
      
      // Check distance to segment end
      const distanceToEnd = Math.abs(timestampMs - seg.endMs);
      if (distanceToEnd < minDistance) {
        minDistance = distanceToEnd;
        closestEdge = seg.endMs;
        isStart = false;
      }
    }
    
    if (closestEdge !== null) return closestEdge;
  }
  
  // No segments on this day - find the closest segment edge from any day
  // but prefer staying on the same day side (before or after)
  let closestEdge = null;
  let minDistance = Infinity;
  let isStart = false;
  
  for (const seg of allSegments.value) {
    // Check distance to segment start
    const distanceToStart = Math.abs(timestampMs - seg.startMs);
    if (distanceToStart < minDistance || (distanceToStart === minDistance && !isStart)) {
      minDistance = distanceToStart;
      closestEdge = seg.startMs;
      isStart = true;
    }
    
    // Check distance to segment end
    const distanceToEnd = Math.abs(timestampMs - seg.endMs);
    if (distanceToEnd < minDistance) {
      minDistance = distanceToEnd;
      closestEdge = seg.endMs;
      isStart = false;
    }
  }

  return closestEdge !== null ? closestEdge : timestampMs;
}

// Helper: Get day string in server timezone
function getDayParts(timestampMs) {
  const fmt = new Intl.DateTimeFormat(undefined, {
    timeZone: props.serverTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return fmt.formatToParts(new Date(timestampMs));
}

// Helper: Calculate video playback position (in seconds) for a given timeline timestamp
// Expects timestampMs to already be snapped to a valid segment
function calculateVideoPosition(timestampMs) {
  const normalizedTimestampMs = normalizeTimestampMs(timestampMs);
  if (!Number.isFinite(normalizedTimestampMs)) {
    console.warn('Invalid timestamp for seek:', timestampMs);
    return 0;
  }

  const segment = findSegmentForTimestamp(normalizedTimestampMs);
  
  if (!segment) {
    console.warn('No segment found for timestamp:', new Date(normalizedTimestampMs).toISOString());
    return 0;
  }
  
  // Get the day of the timestamp we're seeking to
  const targetDay = getDayParts(normalizedTimestampMs);
  const targetDayString = targetDay.find(p => p.type === 'year')?.value + '-' + targetDay.find(p => p.type === 'month')?.value + '-' + targetDay.find(p => p.type === 'day')?.value;
  
  // Only use segments that belong to this selected day from timeline data.
  const dayData = props.timelineData.find(d => (d?.date || d?.day) === targetDayString);
  const sameDaySegments = (dayData?.segments || [])
    .map(normalizeSegment)
    .filter(Boolean)
    .sort((a, b) => a.startMs - b.startMs);
  
  if (sameDaySegments.length === 0) {
    console.warn('No segments found for day:', targetDayString);
    return 0;
  }

  const targetSegment = sameDaySegments.find(seg =>
    normalizedTimestampMs >= seg.startMs && normalizedTimestampMs <= seg.endMs
  );
  if (!targetSegment) return 0;
  
  // Sum up durations of segments before this one (same day only)
  let totalMs = 0;
  for (const seg of sameDaySegments) {
    if (seg.endMs <= targetSegment.startMs) {
      totalMs += (seg.endMs - seg.startMs);
    } else if (seg.startMs < targetSegment.startMs) {
      // Overlapping segment (shouldn't happen but handle it)
      totalMs += Math.min(seg.endMs, targetSegment.startMs) - seg.startMs;
    } else {
      break; // Reached our segment
    }
  }
  
  // Add position within current segment
  const offsetInSegment = normalizedTimestampMs - targetSegment.startMs;
  totalMs += Math.max(0, offsetInSegment);
  
  const dayDurationMs = sameDaySegments.reduce((sum, seg) => sum + (seg.endMs - seg.startMs), 0);
  const videoPositionSec = Math.min(dayDurationMs / 1000, Math.max(0, totalMs / 1000));
  if (videoPositionSec > 86400) {
    console.warn('Clamping oversized seek position:', videoPositionSec, 'for day', targetDayString);
  }
  
  return Math.min(videoPositionSec, 86400);
}

watch(() => props.modelValue, (ms) => { 
  const oldDate = new Date(currentMs.value);
  const newDate = new Date(ms);
  const fmt = new Intl.DateTimeFormat(undefined, {
    timeZone: props.serverTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const oldDateStr = fmt.format(oldDate);
  const newDateStr = fmt.format(newDate);
  if (oldDateStr !== newDateStr) {
    fetchWindow(ms).catch(()=>{}); 
    currentMs.value = ms;
  }
}, { immediate: true });

const paintedSegments = computed(() => {
  const half = actualTimelineWidth.value / 2;
  const pxPerSec = pxPerSecond.value;
  const centerMs = props.modelValue;
  
  if (store.continuousMode) {
    // Continuous mode: render segments side-by-side without gaps
    return paintSegmentsContinuous(half, pxPerSec, centerMs);
  } else {
    // Real-time mode: render segments with actual time gaps
    return paintSegmentsRealtime(half, pxPerSec, centerMs);
  }
});

// Real-time mode rendering (with gaps)
function paintSegmentsRealtime(half, pxPerSec, centerMs) {
  // Add buffer zones for smooth dragging (2x viewport on each side)
  const bufferMultiplier = 2;
  const timeRangeMs = (half / pxPerSec) * 1000;
  const viewStart = centerMs - timeRangeMs * bufferMultiplier;
  const viewEnd = centerMs + timeRangeMs * bufferMultiplier;
  
  const segments = [];
  
  // Render segments in extended range (including buffer)
  for (const seg of allSegments.value) {
    if (seg.endMs < viewStart || seg.startMs > viewEnd) continue;
    
    const s = Math.max(seg.startMs, viewStart);
    const e = Math.min(seg.endMs, viewEnd);
    if (e <= s) continue;
    
    const x = ((s - centerMs) / 1000) * pxPerSec + half;
    const w = ((e - s) / 1000) * pxPerSec;
    
    segments.push({ key: `${seg.startMs}-${seg.endMs}`, x, w });
  }
  
  return segments;
}

// Continuous mode rendering (one continuous bar across all segments)
function paintSegmentsContinuous(half, pxPerSec, centerMs) {
  // Use all segments sorted by time
  const sortedSegments = [...allSegments.value].sort((a, b) => a.startMs - b.startMs);
  
  if (sortedSegments.length === 0) return [];
  
  // Calculate total duration of all segments across all days
  const totalDuration = sortedSegments.reduce((sum, seg) => sum + (seg.endMs - seg.startMs), 0);
  
  // Find which segment contains the center timestamp and cumulative position
  const centerSegment = findSegmentForTimestamp(centerMs);
  if (!centerSegment) return [];
  
  let cumulativeMs = 0;
  for (const seg of sortedSegments) {
    if (seg.startMs >= centerSegment.startMs) break;
    cumulativeMs += (seg.endMs - seg.startMs);
  }
  
  // Add offset within current segment
  const offsetInSegment = centerMs - centerSegment.startMs;
  const centerContinuousMs = cumulativeMs + offsetInSegment;
  
  // Calculate visible range in continuous time
  const bufferMultiplier = 2;
  const timeRangeMs = (half / pxPerSec) * 1000;
  const viewStart = centerContinuousMs - timeRangeMs * bufferMultiplier;
  const viewEnd = centerContinuousMs + timeRangeMs * bufferMultiplier;
  
  // Render one continuous bar spanning all recordings
  const visibleStart = Math.max(0, viewStart);
  const visibleEnd = Math.min(totalDuration, viewEnd);
  
  if (visibleEnd > visibleStart) {
    const x = ((visibleStart - centerContinuousMs) / 1000) * pxPerSec + half;
    const w = ((visibleEnd - visibleStart) / 1000) * pxPerSec;
    
    return [{ key: 'continuous-bar', x, w }];
  }
  
  return [];
}

// Cache nice intervals array to avoid recreating it on every computation
const NICE_INTERVALS = [
  1,      // 1 second
  2,      // 2 seconds
  5,      // 5 seconds
  10,     // 10 seconds
  15,     // 15 seconds
  30,     // 30 seconds
  60,     // 1 minute
  120,    // 2 minutes
  300,    // 5 minutes
  600,    // 10 minutes
  900,    // 15 minutes
  1800,   // 30 minutes
  3600,   // 1 hour
  7200,   // 2 hours
  14400,  // 4 hours
  21600,  // 6 hours
  43200,  // 12 hours
];

const visibleTicks = computed(() => {
  const half = actualTimelineWidth.value / 2;
  const pxPerSec = pxPerSecond.value;
  const centerMs = props.modelValue;
  
  // Add buffer for ticks too (2x viewport on each side, matching segments)
  const bufferMultiplier = 2;
  const timeRangeMs = (half / pxPerSec) * 1000;
  const viewStart = centerMs - timeRangeMs * bufferMultiplier;
  const viewEnd = centerMs + timeRangeMs * bufferMultiplier;

  // Calculate total visible time in seconds (use unbuffered for tick calculation)
  const totalVisibleSeconds = (timeRangeMs * 2) / 1000;
  
  // Find the best interval to show ~8-12 ticks
  let stepSec = NICE_INTERVALS[NICE_INTERVALS.length - 1];
  
  for (let i = 0; i < NICE_INTERVALS.length; i++) {
    const interval = NICE_INTERVALS[i];
    const tickCount = totalVisibleSeconds / interval;
    const targetMax = interval < 60 ? 14 : 10;
    if (tickCount <= targetMax) {
      stepSec = interval;
      break;
    }
  }
  
  const showSeconds = stepSec < 60;
  const startSec = Math.floor(viewStart / 1000);
  const endSec = Math.ceil(viewEnd / 1000);
  const first = Math.floor(startSec / stepSec) * stepSec;

  const fmt = new Intl.DateTimeFormat(undefined, {
    timeZone: props.serverTz,
    hour: '2-digit',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
    hour12: false
  });

  const ticks = [];
  for (let s = first; s <= endSec; s += stepSec) {
    const ms = s * 1000;
    const px = ((ms - centerMs) / 1000) * pxPerSec + half;
    const parts = fmt.formatToParts(new Date(ms));
    const hh = parts.find(p => p.type === 'hour')?.value ?? '00';
    const mm = parts.find(p => p.type === 'minute')?.value ?? '00';
    const ss = showSeconds ? (parts.find(p => p.type === 'second')?.value ?? '00') : null;
    
    const label = showSeconds ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
    const major = showSeconds ? (s % 60 === 0) : (s % 3600 === 0);
    ticks.push({ key: ms, px, label, major });
  }
  return ticks;
});

// interactions
let dragging = false;
let dragStartX = 0;
let dragStartEpoch = 0;
const dragOffset = ref(0); // Visual drag offset in pixels (for CSS transform)

function onDragStart(e) {
  dragging = true;
  dragStartX = (e.touches ? e.touches[0].clientX : e.clientX);
  dragStartEpoch = props.modelValue;
  dragOffset.value = 0;
  suppressTimelineSync.value = true;
  window.addEventListener('mousemove', onDragMove);
  window.addEventListener('mouseup', onDragEnd);
  window.addEventListener('touchmove', onDragMove, { passive: false });
  window.addEventListener('touchend', onDragEnd);
}

function onDragMove(e) {
  if (!dragging) return;
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const dx = clientX - dragStartX;
  
  // Only update visual offset during drag - no recalculation needed!
  dragOffset.value = dx;
  
  e.preventDefault?.();
}
function onDragEnd() {
  dragging = false;
  window.removeEventListener('mousemove', onDragMove);
  window.removeEventListener('mouseup', onDragEnd);
  window.removeEventListener('touchmove', onDragMove);
  window.removeEventListener('touchend', onDragEnd);
  
  // Calculate final timestamp from drag offset
  const dSec = -dragOffset.value / pxPerSecond.value;
  const finalMs = dragStartEpoch + dSec * 1000;
  
  // Reset visual offset
  dragOffset.value = 0;
  
  // Snap to nearest valid segment (respects day boundaries)
  const snappedMs = snapToNearestSegment(finalMs);
  
  // Update model value to snapped position
  emit('update:modelValue', snappedMs);
  
  // Get the day of the snapped timestamp
  const dayParts = getDayParts(snappedMs);
  const dayStr = `${dayParts.find(p => p.type === 'year')?.value}-${dayParts.find(p => p.type === 'month')?.value}-${dayParts.find(p => p.type === 'day')?.value}`;
  
  // Calculate video position for the snapped timestamp
  const videoPositionSec = calculateVideoPosition(snappedMs);
  
  // Only emit seek if we have a valid video position
  if (videoPositionSec >= 0) {
    // Update current playback day for sync
    currentPlaybackDay.value = dayStr;
    
    suppressTimelineSync.value = true;
    emit('seek', { timestampMs: snappedMs, videoPositionSec, day: dayStr });
  } else {
    console.error('Failed to calculate video position for timestamp:', new Date(snappedMs).toISOString());
  }
}

function onTouchStart(e) { onDragStart(e); }

function onWheel(e) {
  // Simplified zoom: scroll to zoom in/out gradually
  // Zoom in when scrolling up (negative deltaY), zoom out when scrolling down (positive deltaY)
  const zoomDirection = e.deltaY > 0 ? -1 : 1; // -1 = zoom out, 1 = zoom in
  const zoomIncrement = 0.015; // Small incremental change (1.5% per scroll tick)
  
  // Calculate new zoom level
  let newZoom = pxPerSecond.value + (zoomDirection * zoomIncrement);
  
  // Clamp between min and max
  newZoom = Math.min(maxZoom, Math.max(minZoom, newZoom));
  
  pxPerSecond.value = newZoom;
}

watch(() => props.cameraId, async () => {
  await fetchWindow(props.modelValue);
}, { immediate: true });

watch(() => props.timelineData, async () => {
  await fetchWindow();
}, { immediate: true });

// Update timeline width
function updateTimelineWidth() {
  // Check both timeline elements and use whichever is visible (has width > 0)
  let width = 0;
  
  if (timelineContainerEl.value && timelineContainerEl.value.clientWidth > 0) {
    width = timelineContainerEl.value.clientWidth;
  } else if (timelineDesktopEl.value && timelineDesktopEl.value.clientWidth > 0) {
    width = timelineDesktopEl.value.clientWidth;
  }
  
  if (width > 0) {
    actualTimelineWidth.value = width;
  }
}

onMounted(async () => {
  await fetchWindow(props.modelValue);
  // Click outside to close day picker
  document.addEventListener('click', handleClickOutside);
  
  // Update timeline width on mount
  updateTimelineWidth();
  
  // Initialize current playback day
  currentPlaybackDay.value = currentDayString.value;
  
  // Watch for both timeline container resizes
  const resizeObserver = new ResizeObserver(() => {
    updateTimelineWidth();
  });
  
  if (timelineContainerEl.value) {
    resizeObserver.observe(timelineContainerEl.value);
  }
  
  if (timelineDesktopEl.value) {
    resizeObserver.observe(timelineDesktopEl.value);
  }
  
  // Store observer for cleanup
  if (timelineContainerEl.value) {
    timelineContainerEl.value._resizeObserver = resizeObserver;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  
  // Cleanup video event listeners
  if (props.videoElement && videoEventListeners.length > 0) {
    videoEventListeners.forEach(({ event, handler }) => {
      props.videoElement.removeEventListener(event, handler);
    });
    videoEventListeners = [];
  }
  
  // Cleanup resize observer
  if (timelineContainerEl.value && timelineContainerEl.value._resizeObserver) {
    timelineContainerEl.value._resizeObserver.disconnect();
  }
});

// Day selector functionality
const showDayPicker = ref(false);
const dayPickerEl = ref(null);

// Get current day string from modelValue
const currentDayString = computed(() => {
  const parts = getDayParts(props.modelValue);
  return `${parts.find(p => p.type === 'year')?.value}-${parts.find(p => p.type === 'month')?.value}-${parts.find(p => p.type === 'day')?.value}`;
});

// Format current day for display
const currentDayFormatted = computed(() => {
  try {
    const fmt = new Intl.DateTimeFormat(undefined, {
      timeZone: props.serverTz,
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    return fmt.format(new Date(props.modelValue));
  } catch {
    return currentDayString.value;
  }
});


// Format current day in short format (e.g., "Oct 1")
const currentDayShort = computed(() => {
  try {
    const fmt = new Intl.DateTimeFormat(undefined, {
      timeZone: props.serverTz,
      month: 'short',
      day: 'numeric'
    });
    return fmt.format(new Date(props.modelValue));
  } catch {
    return currentDayString.value;
  }
});

// Get available days from timeline data
const availableDays = computed(() => {
  return props.timelineData.map(dayData => {
    const dateString = dayData.date || dayData.day;
    return {
      dateString,
      segments: dayData.segments
    };
  }).sort((a, b) => a.dateString.localeCompare(b.dateString)); // Sort oldest first (chronological order)
});

// Check if there's a previous/next day
const hasPreviousDay = computed(() => {
  const currentIndex = availableDays.value.findIndex(d => d.dateString === currentDayString.value);
  return currentIndex > 0; // Can go to earlier date (lower index)
});

const hasNextDay = computed(() => {
  const currentIndex = availableDays.value.findIndex(d => d.dateString === currentDayString.value);
  return currentIndex < availableDays.value.length - 1; // Can go to later date (higher index)
});

// Toggle day picker
function toggleDayPicker(e) {
  e.stopPropagation();
  showDayPicker.value = !showDayPicker.value;
}

// Close day picker when clicking outside
function handleClickOutside(e) {
  if (dayPickerEl.value && !dayPickerEl.value.contains(e.target)) {
    showDayPicker.value = false;
  }
}

// Format day option for picker
function formatDayOption(dateString) {
  try {
    const [year, month, day] = dateString.split('-');
    const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
    const fmt = new Intl.DateTimeFormat(undefined, {
      timeZone: props.serverTz,
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    return fmt.format(date);
  } catch {
    return dateString;
  }
}

// Navigate to previous day (earlier date)
function goToPreviousDay() {
  const currentIndex = availableDays.value.findIndex(d => d.dateString === currentDayString.value);
  if (currentIndex > 0) {
    const prevDay = availableDays.value[currentIndex - 1];
    selectDay(prevDay.dateString);
  }
}

// Navigate to next day (later date)
function goToNextDay() {
  const currentIndex = availableDays.value.findIndex(d => d.dateString === currentDayString.value);
  if (currentIndex < availableDays.value.length - 1) {
    const nextDay = availableDays.value[currentIndex + 1];
    selectDay(nextDay.dateString);
  }
}

// Select a specific day
function selectDay(dateString) {
  showDayPicker.value = false;
  
  // Parse the date string and create a timestamp at the start of that day
  const [year, month, day] = dateString.split('-');
  const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0));
  const dayStartMs = date.getTime();
  
  // Find the first segment of that day and jump to it
  const daySegments = allSegments.value.filter(seg => {
    const segDay = getDayParts(seg.startMs);
    const segDayStr = `${segDay.find(p => p.type === 'year')?.value}-${segDay.find(p => p.type === 'month')?.value}-${segDay.find(p => p.type === 'day')?.value}`;
    return segDayStr === dateString;
  });
  
  // Update current playback day for sync
  currentPlaybackDay.value = dateString;
  
  if (daySegments.length > 0) {
    // Jump to the start of the first segment of that day
    const targetMs = daySegments[0].startMs;
    emit('update:modelValue', targetMs);
    
    // Calculate video position and emit seek event
    const videoPositionSec = calculateVideoPosition(targetMs);
    suppressTimelineSync.value = true;
    emit('seek', { timestampMs: targetMs, videoPositionSec, day: dateString });
  } else {
    // No segments for this day, just jump to start of day
    emit('update:modelValue', dayStartMs);
  }
}
</script>

<style scoped>
.controls-wrapper { 
  display: flex; 
  flex-direction: column;
  gap: 0;
}

/* Mobile timeline wrapper - hidden by default (shown only on portrait) */
.timeline-mobile-wrapper {
  display: none;
}

/* Desktop timeline - shown by default */
.timeline-desktop {
  display: block;
}

.timeline-mobile {
  display: none;
}

/* Portrait/Mobile layout: Show mobile timeline, hide desktop timeline */
@media (max-aspect-ratio: 9/16), (max-width: 768px) {
  /* Show mobile timeline above controls */
  .timeline-mobile-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    background: rgba(12, 16, 23, 0.95);
    border: 1px solid rgba(29, 38, 51, 0.8);
    border-radius: 6px;
    backdrop-filter: blur(10px);
    margin-bottom: 6px;
  }

  .timeline-mobile {
    display: block;
    flex: 1;
  }

  /* Hide desktop timeline from controls bar */
  .timeline-desktop {
    display: none;
  }

  /* Adjust control button sizes for mobile */
  .control-btn {
    width: 36px;
    height: 36px;
  }

  /* Smaller day selector on mobile */
  .day-selector-compact {
    gap: 2px;
  }

  .day-nav-btn-small {
    width: 24px;
    height: 24px;
  }

  .day-button {
    padding: 4px 8px;
    font-size: 12px;
  }

  .day-button svg {
    width: 14px;
    height: 14px;
  }

  .controls-bar {
    padding: 8px 10px;
  }

  /* Hide day short text on very small screens */
  @media (max-width: 400px) {
    .day-short {
      display: none;
    }
  }
}

/* Compact Day Selector */
.day-selector-compact {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.day-nav-btn-small {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: #1a2332;
  color: #9aa4b2;
  border: 1px solid #2a3547;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.day-nav-btn-small .mdi {
  font-size: 16px;
  line-height: 1;
}

.day-nav-btn-small:hover:not(:disabled) {
  background: #243349;
  color: #e8edf3;
  border-color: #3a465a;
}

.day-nav-btn-small:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.day-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #1a2332;
  color: #e8edf3;
  border: 1px solid #2a3547;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  white-space: nowrap;
}

.day-button .mdi {
  font-size: 16px;
  line-height: 1;
}

.day-button:hover {
  background: #243349;
  border-color: #3a465a;
  color: #8ba3c7;
}

.day-short {
  font-weight: 500;
}

/* Day Picker Dropdown */
.day-picker {
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  max-height: 300px;
  min-width: 240px;
  overflow-y: auto;
  background: #1a2332;
  border: 1px solid #2a3547;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
}

.day-option {
  padding: 10px 16px;
  color: #9aa4b2;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  border-bottom: 1px solid #0c1017;
}

.day-option:last-child {
  border-bottom: none;
}

.day-option:hover {
  background: #243349;
  color: #e8edf3;
}

.day-option.active {
  background: rgba(107, 139, 179, 0.2);
  color: #8ba3c7;
  font-weight: 500;
  border: 1px solid #6b8bb3;
}

/* Custom scrollbar for day picker */
.day-picker::-webkit-scrollbar {
  width: 8px;
}

.day-picker::-webkit-scrollbar-track {
  background: #0c1017;
  border-radius: 6px;
}

.day-picker::-webkit-scrollbar-thumb {
  background: #2a3547;
  border-radius: 6px;
}

.day-picker::-webkit-scrollbar-thumb:hover {
  background: #3a465a;
}

/* Controls Bar */
.controls-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(12, 16, 23, 0.95);
  border: 1px solid rgba(29, 38, 51, 0.8);
  border-radius: 6px;
  backdrop-filter: blur(10px);
}

.control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: #1a2332;
  color: #e8edf3;
  border: 1px solid #2a3547;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.control-btn .mdi {
  font-size: 20px;
  line-height: 1;
}

.control-btn:hover {
  background: #243349;
  border-color: #3a465a;
  color: #8ba3c7;
}

.control-btn:active {
  transform: scale(0.95);
}

.control-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.day-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

/* Zoom button active state */
.control-btn.zoom-active {
  background: rgba(107, 139, 179, 0.3);
  border-color: rgba(139, 163, 199, 0.5);
  color: #8ba3c7;
}

.control-btn.zoom-active:hover {
  background: rgba(107, 139, 179, 0.5);
  border-color: #8ba3c7;
  color: #ffffff;
}

/* Playback Speed Controls */
.speed-control-wrapper {
  position: relative;
}

.control-btn.speed {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 60px;
}

.speed-label {
  font-size: 12px;
  font-weight: 600;
}

.control-btn.speed-active {
  background: rgba(107, 139, 179, 0.3);
  border-color: rgba(139, 163, 199, 0.5);
  color: #8ba3c7;
}

.control-btn.speed-active:hover {
  background: rgba(107, 139, 179, 0.5);
  border-color: #8ba3c7;
  color: #ffffff;
}

.speed-slider-popup {
  position: absolute;
  bottom: 50px;
  right: 0;
  width: 280px;
  background: rgba(12, 16, 23, 0.98);
  border: 1px solid rgba(29, 38, 51, 0.8);
  border-radius: 8px;
  backdrop-filter: blur(10px);
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.2s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.speed-slider-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(29, 38, 51, 0.8);
  color: #e8edf3;
  font-weight: 600;
  font-size: 13px;
}

.speed-close {
  background: none;
  border: none;
  color: #e8edf3;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.speed-close:hover {
  color: #8ba3c7;
}

.speed-close .mdi {
  font-size: 20px;
}

.speed-slider-body {
  padding: 16px;
}

.speed-presets {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.speed-preset-btn {
  flex: 1;
  padding: 8px 12px;
  background: #1a2332;
  color: #e8edf3;
  border: 1px solid #2a3547;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
}

.speed-preset-btn:hover {
  background: #243349;
  border-color: #3a465a;
  color: #ffffff;
}

.speed-preset-btn.active {
  background: rgba(107, 139, 179, 0.3);
  border-color: #8ba3c7;
  color: #8ba3c7;
}

.speed-slider-container {
  margin-top: 16px;
}

.speed-slider {
  width: 100%;
  height: 6px;
  background: linear-gradient(to right, 
    #1a2332 0%, 
    #8ba3c7 50%, 
    #6b8bb3 100%
  );
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.speed-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: #8ba3c7;
  border: 2px solid #ffffff;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.speed-slider::-webkit-slider-thumb:hover {
  background: #6b8bb3;
  transform: scale(1.2);
}

.speed-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #8ba3c7;
  border: 2px solid #ffffff;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.speed-slider::-moz-range-thumb:hover {
  background: #6b8bb3;
  transform: scale(1.2);
}

.speed-markers {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  color: #888;
  font-size: 11px;
}

/* Timeline Container */
.timeline-container {
  flex: 1;
  min-width: 0;
}

.timeline-new { 
  position: relative; 
  height: 60px; 
  background: #0c1017;
  border: 1px solid #1d2633; 
  border-radius: 4px; 
  overflow: hidden; 
  user-select: none;
}

.timeline-disabled {
  opacity: 0.4;
  pointer-events: none;
  cursor: not-allowed;
}

/* Timeline Skeleton Loader */
.timeline-skeleton {
  position: relative;
  height: 60px;
  background: #0c1017;
  border: 1px solid #1d2633;
  border-radius: 4px;
  overflow: hidden;
}

.skeleton-segments {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
}

.skeleton-segment {
  position: absolute;
  top: 20px;
  height: 20px;
  background: linear-gradient(90deg, rgba(107, 139, 179, 0.2), rgba(139, 163, 199, 0.3));
  border-radius: 2px;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-segment:nth-child(odd) {
  animation-delay: 0.2s;
}

.skeleton-segment:nth-child(even) {
  animation-delay: 0.4s;
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.7;
  }
}

.skeleton-center-line {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  background: rgba(139, 163, 199, 0.5);
  z-index: 2;
}

.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.05) 50%,
    transparent 100%
  );
  animation: skeleton-shimmer 2s infinite;
  z-index: 3;
}

@keyframes skeleton-shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.timeline-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  will-change: transform;
}

.center-line { 
  position: absolute; 
  top: 0; 
  bottom: 0; 
  left: 50%; 
  width: 2px; 
  background: #8ba3c7;
  z-index: 2;
  box-shadow: 0 0 4px rgba(139, 163, 199, 0.5);
}

.segments, .ticks { 
  position: absolute; 
  top: 0; 
  left: 0; 
  right: 0; 
  height: 100%; 
}

.segment { 
  position: absolute; 
  top: 20px; 
  height: 20px; 
  background: linear-gradient(90deg, #6b8bb3, #8ba3c7); 
  border-radius: 2px; 
}

.tick { 
  position: absolute; 
  bottom: 0; 
  transform: translateX(-50%); 
  color: #9aa4b2; 
  font-size: 9px; 
  text-align: center; 
  white-space: nowrap; 
}

.tick-line { 
  width: 1px; 
  height: 6px; 
  background: #293242; 
  margin: 0 auto; 
}

.tick-line.major { 
  height: 10px; 
  background: #3a465a; 
}

.tick-label { 
  margin-top: 1px; 
}

/* Time Display */
.time-display {
  padding: 8px 12px;
  background: #0c1017;
  border: 1px solid #1d2633;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  color: #e8edf3;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>

